/**
 * @fileoverview Present a selected calendar event or AI proposal with explicit responsibility and review actions. Backend records own attachments, assignments, and valid transitions; the parent owns accepted updates. `kc-internal-frontend/src/components/AIDeadlines/DeadlineReplacementPicker.js` supplies replacement context. Viewing an attachment or proposal does not acknowledge responsibility or publish a deadline.
 */

import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import Button from "../shared/AppPrimeButton";
import api from '../../api';
import Avatar from '../Avatar/Avatar';
import AppIcon from "../shared/AppIcon";
import DeadlineReplacementPicker from '../AIDeadlines/DeadlineReplacementPicker';



const EventDetailsModal = ({
  event,
  onClose,
  onEdit,
  onDelete,
  onConfirmResponsibility,
  onApproveAiDeadline,
  onRejectAiDeadline
}) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isApprovingAi, setIsApprovingAi] = useState(false);
  const [isRejectingAi, setIsRejectingAi] = useState(false);
  const [replacement, setReplacement] = useState(null);
  const [approvalError, setApprovalError] = useState('');
  useEffect(() => { setReplacement(null); setApprovalError(''); }, [event?.id]);



  const getCategoryColor = () => {
    if (!event) {
      return 'gray';
    }

    // First try to use the category_color field directly
    if (event.category_color) {
      return event.category_color.toLowerCase();
    }

    // Fallback: Map category names to colors (matching the calendar categories)
    if (event.category_name) {
      const categoryColorMap = {
        'Our Deadline': 'red',
        'Opponent Deadline': 'green',
        'Court/Arbitrator Deadline': 'purple',
        'Our Appearance Required': 'yellow',
        'Anything Else': 'blue',
        'AI Deadlines': 'blue' // AI-detected deadlines use blue
      };

      return categoryColorMap[event.category_name] || 'gray';
    }

    return 'gray';
  };

  useEffect(() => {
    let isMounted = true;
    api.get('/user')
    .then((res) => {if (isMounted) setCurrentUser(res.data);})
    .catch(() => {});
    return () => {isMounted = false;};
  }, []);

  // Fetch event attachments (if any) whenever event changes
  useEffect(

  () => {
    let active = true;
    const fetchAttachments =

    async () => {
      try {
        const eventId = event?.id == null ? '' : String(event.id);
        if (!eventId || !/^\d+$/.test(eventId)) {
          setAttachments([]);
          return;
        }
        setIsLoadingAttachments(true);
        const res = await api.get(`/events/${eventId}/attachments`);
        if (!active) return;
        setAttachments(res.data.attachments || []);
      } catch (e) {
        if (!active) return;
        setAttachments([]);
      } finally {
        if (active) setIsLoadingAttachments(false);
      }
    };
    fetchAttachments();
    return () => {active = false;};
  }, [event]);

  if (!event) return null;

  const {
    title,
    description,
    start,
    end,
    allDay,
    assignees,
    category_name,
    type,
    confirmed,
    case: caseNumber, // de-structure 'case' property and rename to caseNumber
    source,
    ai_status,
    ai_confidence,
    ai_reasoning
  } = event;



  const isAiEvent = source === 'ai-calendar';



  const userIsAssignee = !!(currentUser && Array.isArray(assignees) && assignees.some(

  (a) => {
    console.log('[EventDetailsModal] Checking assignee:', {
      currentUserEmail: currentUser.Email,
      currentUserName: currentUser.Name,
      attorneyEmail: a.attorney_email,
      attorneyName: a.attorney_name
    });

    // Primary check: compare emails (case-insensitive)
    if (currentUser.Email && a.attorney_email) {
      const match = currentUser.Email.toLowerCase() === a.attorney_email.toLowerCase();
      console.log('[EventDetailsModal] Email comparison:', match);
      return match;
    }
    // Fallback: compare names (case-insensitive and trimmed)
    if (currentUser.Name && a.attorney_name) {
      const match = currentUser.Name.trim().toLowerCase() === a.attorney_name.trim().toLowerCase();
      console.log('[EventDetailsModal] Name comparison:', match);
      return match;
    }
    console.log('[EventDetailsModal] No match found');
    return false;
  }));

  console.log('[EventDetailsModal] userIsAssignee:', userIsAssignee, 'confirmed:', confirmed);

  // (moved earlier to avoid conditional hook)

  const renderDateTime =

  () => {
    // For all-day events, parse as UTC to preserve the calendar date (avoids timezone shift)
    const startDate = allDay ? moment.utc(start).local(true) : moment(start);
    const endDate = allDay ? moment.utc(end).local(true) : moment(end);

    if (allDay) {
      if (startDate.isSame(endDate, 'day')) {
        return <p className="p-field"><strong>Date:</strong> {startDate.format('MMMM Do YYYY')} (All Day)</p>;
      }
      return (
        <>
                    <p className="p-field"><strong>From:</strong> {startDate.format('MMMM Do YYYY')} (All Day)</p>
                    <p className="p-field"><strong>To:</strong> {endDate.format('MMMM Do YYYY')} (All Day)</p>
                </>);

    }

    if (startDate.isSame(endDate, 'day')) {
      return (
        <>
                    <p className="p-field"><strong>Date:</strong> {startDate.format('MMMM Do YYYY')}</p>
                    <p className="p-field"><strong>Time:</strong> {`${startDate.format('h:mm a')} - ${endDate.format('h:mm a')}`}</p>
                </>);

    }

    return (
      <>
                <p className="p-field"><strong>From:</strong> {startDate.format('MMMM Do YYYY, h:mm a')}</p>
                <p className="p-field"><strong>To:</strong> {endDate.format('MMMM Do YYYY, h:mm a')}</p>
            </>);

  };



  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not on child elements
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const categoryColor = getCategoryColor();

  return (
    <div className="event-form-overlay" onClick={handleOverlayClick}>
            <div className={`event-form-container event-details-modal glass-modal glass-${categoryColor}`}>
                <div className="modal-header">
                    <h2>{title || 'Event Details'}</h2>
                    <Button icon="pi pi-times" className="p-button-rounded p-button-text" onClick={onClose} />
                </div>
                <div className="modal-content p-fluid">
                    {event.google_sync?.status === 'pending' && (
                        <p role="status" className="calendar-sync-notice"><AppIcon icon="pi pi-sync" size={16} /> Google Calendar sync pending</p>
                    )}
                    {description && <p className="p-field"><strong>Description:</strong> {description}</p>}
                    {renderDateTime()}
                    <p className="p-field"><strong>Category:</strong> {category_name || 'N/A'}</p>
                    <p className="p-field"><strong>Case:</strong> {caseNumber || 'N/A'}</p>
                    {type && <p className="p-field"><strong>Type:</strong> {type}</p>}
                    <p className="p-field">
                        <strong>Status:</strong>{' '}
                        {isAiEvent ?
            ai_status === 'PENDING_APPROVAL' ?
            <span className="status-unconfirmed" style={{ color: '#ff9800' }}>🤖 Pending Approval</span> :
            ai_status === 'APPROVED' ?
            <span className="status-unconfirmed" style={{ color: '#2196f3' }}>🤖 Approved (not yet converted to event)</span> :

            <span className="status-confirmed" style={{ color: '#4caf50' }}>🤖 Converted</span> :

            confirmed ?
            <span className="status-confirmed">Confirmed</span> :

            <span className="status-unconfirmed">Unconfirmed</span>}

                    </p>
                    {isAiEvent && ['PENDING_APPROVAL', 'APPROVED'].includes(ai_status) && <>
                      <DeadlineReplacementPicker deadlineId={String(event.id).replace('ai-deadline-', '')}
                        replacement={replacement} onChange={setReplacement} disabled={isApprovingAi} />
                      {approvalError && <p role="alert">{approvalError}</p>}
                    </>}
                    {/* Show AI-specific metadata for AI-detected deadlines */}
                    {isAiEvent && ai_confidence &&
          <p className="p-field">
                            <strong>AI Confidence:</strong>{' '}
                            <span style={{
              color: ai_confidence >= 0.8 ? '#4caf50' : ai_confidence >= 0.5 ? '#ff9800' : '#f44336'
            }}>
                                {Math.round(ai_confidence * 100)}%
                            </span>
                        </p>}

                    {isAiEvent && ai_reasoning &&
          <p className="p-field">
                            <strong>AI Reasoning:</strong> {ai_reasoning}
                        </p>}


                    <div className="p-field">
                        <strong>Assignees:</strong>
                        {assignees && assignees.length > 0 ?
            <div className="p-chips-multiple-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                                {assignees.map((a) => {
                // Custom chip rendering to support Google profile pictures with referrerPolicy
                // PrimeReact's Chip image prop doesn't support the necessary attributes
                const hasProfilePic = a.attorney_profile_pic &&
                typeof a.attorney_profile_pic === 'string' &&
                a.attorney_profile_pic.trim() !== '';

                const getInitials =

                (name) => {
                  if (!name) return '?';
                  return name.split(' ')
                  .map((n) => n[0])
                  .join('')
                  .substring(0, 2)
                  .toUpperCase();
                };

                return (
                  <div
                    key={a.attorney_id}
                    className="glass-chip assignee-chip"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      backgroundColor: 'var(--surface-100)',
                      border: '1px solid var(--surface-300)',
                      fontSize: '14px'
                    }}>

                                            {hasProfilePic ?
                    <img
                      src={a.attorney_profile_pic}
                      alt={a.attorney_name}
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                      onError={

                      (e) => {
                        // Fall back to initials on error
                        const initials = getInitials(a.attorney_name);
                        const parent = e.target.parentNode;
                        const initialsDiv = document.createElement('div');
                        initialsDiv.textContent = initials;
                        Object.assign(initialsDiv.style, {
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: '#6366f1',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        });
                        parent.replaceChild(initialsDiv, e.target);
                      }} /> :


                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: '#6366f1',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>
                                                    {getInitials(a.attorney_name)}
                                                </div>}

                                            <span>{a.attorney_name}</span>
                                        </div>);

              })}
                            </div> :

            <span>N/A</span>}

                    </div>
                    <div className="p-field">
                        <strong>Attachments:</strong>
                        {isLoadingAttachments ?
            <span> Loading...</span> :
            attachments && attachments.length > 0 ?
            <ul style={{ paddingLeft: '18px', marginTop: '6px' }}>
                                {attachments.map((att, idx) =>
              <li key={idx} style={{ marginBottom: '6px' }}>
                                        {att.iconLink &&
                <img src={att.iconLink} alt="icon" style={{ width: 16, height: 16, verticalAlign: 'middle', marginRight: 6 }} />}

                                        <a href={att.fileUrl} target="_blank" rel="noopener noreferrer" style={{ marginRight: 8 }}>
                                            {att.title || 'Attachment'}
                                        </a>
                                        <Button
                  label="Download"
                  icon="pi pi-download"
                  className="p-button-text p-button-sm"
                  onClick={() => {
                    const url = att.downloadUrl || att.fileUrl;
                    if (url) window.open(url, '_blank');
                  }} />

                                    </li>
              )}
                            </ul> :

            <span> None</span>}

                    </div>
                </div>
                <div className="modal-actions">
                    <Button
            label="Close"
            icon="pi pi-times"
            onClick={onClose}
            className="p-button-text"
            disabled={isDeleting || isConfirming || isApprovingAi || isRejectingAi} />

                    {/* Show Edit button only for non-AI events or approved AI events */}
                    {(!isAiEvent || event.ai_status === 'APPROVED') &&
          <Button
            label="Edit"
            icon="pi pi-pencil"
            onClick={onEdit}
            disabled={isDeleting || isConfirming || isApprovingAi || isRejectingAi} />}


                    {/* Show Delete button only for non-AI events */}
                    {onDelete && !isAiEvent &&
          <Button
            label="Delete"
            icon={isDeleting ? "pi pi-spin pi-spinner" : "pi pi-trash"}
            severity="danger"
            disabled={isDeleting || isConfirming || isApprovingAi || isRejectingAi}
            loading={isDeleting}
            onClick={

            async () => {
              if (window.confirm('Are you sure you want to delete this event?')) {
                setIsDeleting(true);
                try {
                  await onDelete(event.id);
                  if (onClose) onClose();
                } finally {
                  setIsDeleting(false);
                }
              }
            }} />}


                    {/* 
             Show Accept button for AI deadlines that haven't been converted yet.
             This includes both PENDING_APPROVAL (new) and APPROVED (legacy from Slack).
             Both need to be converted to real CalendarEvents with assignees.
            */}
                    {isAiEvent && (event.ai_status === 'PENDING_APPROVAL' || event.ai_status === 'APPROVED') && onApproveAiDeadline &&
          <Button
            label={isApprovingAi ? "Saving..." : replacement ? "Replace & Approve" : "Accept & Convert"}
            icon={isApprovingAi ? "pi pi-spin pi-spinner" : "pi pi-check"}
            severity="success"
            disabled={isDeleting || isConfirming || isApprovingAi || isRejectingAi}
            loading={isApprovingAi}
            onClick={

            async () => {
              setIsApprovingAi(true);
              setApprovalError('');
              try {
                await onApproveAiDeadline(event.id, replacement ? {id: replacement.id, version: replacement.version} : null);
                if (onClose) onClose();
              } catch (error) {
                setApprovalError(error.response?.data?.error || error.message);
              } finally {
                setIsApprovingAi(false);
              }
            }} />}


                    {/* Show Pass button only for pending (not yet approved) AI deadlines */}
                    {isAiEvent && event.ai_status === 'PENDING_APPROVAL' && onRejectAiDeadline &&
          <Button
            label={isRejectingAi ? "Passing..." : "Pass"}
            icon={isRejectingAi ? "pi pi-spin pi-spinner" : "pi pi-times"}
            severity="warning"
            disabled={isDeleting || isConfirming || isApprovingAi || isRejectingAi}
            loading={isRejectingAi}
            onClick={

            async () => {
              if (window.confirm('Are you sure you want to pass on this AI-detected deadline? It will be removed from the calendar.')) {
                setIsRejectingAi(true);
                try {
                  await onRejectAiDeadline(event.id);
                  if (onClose) onClose();
                } finally {
                  setIsRejectingAi(false);
                }
              }
            }} />}


                    {!confirmed && userIsAssignee && !isAiEvent &&
          <Button
            severity="success"
            disabled={isDeleting || isConfirming || isApprovingAi || isRejectingAi}
            loading={isConfirming}
            onClick={

            async () => {
              if (onConfirmResponsibility) {
                setIsConfirming(true);
                try {
                  await onConfirmResponsibility(event.id);
                  if (onClose) onClose();
                } finally {
                  setIsConfirming(false);
                }
              }
            }}>

                            {isConfirming ?
            <>
                                    <AppIcon icon="pi pi-spin pi-spinner" style={{ marginRight: '0.5rem' }} />
                                    Confirming...
                                </> :

            <>
                                    <Avatar
                src={currentUser?.ProfilePic}
                name={currentUser?.Name}
                size={24}
                style={{ marginRight: '0.5rem' }} />

                                    Confirm Responsibility
                                </>}

                        </Button>}

                </div>
            </div>
        </div>);

};

export default EventDetailsModal;
